<template>
    <PrimeButton
        ref="button"
        v-bind="$attrs"
        variant="secondary"
        label="Try again"
        class="retry"
        :aria-disabled="busy ? 'true' : undefined"
        :aria-describedby="failureId"
        @click="press"
    >
        <template #icon>
            <PbScanMark v-if="busy" />
            <PhArrowClockwise v-else :size="16" aria-hidden="true" />
        </template>
    </PrimeButton>
    <!-- What this button retries, as its description, so two retries on one screen are two different buttons to a
         screen reader. Hidden: the failure is already on screen beside the button. -->
    <span :id="failureId" hidden>{{ failure }}</span>
</template>

<script setup lang="ts">
/**
 * Asks again for something that failed to arrive, and owns everything about asking.
 *
 * Busy: while the request is out the button stays where it is, with its label and the scan mark in its icon's place,
 * and refuses presses, so one press is one request however many times it is pressed. Busy is aria-disabled rather than
 * disabled because a browser blurs a disabled button, and the member who pressed it keeps their focus on it.
 *
 * Heard: the failure is said through the app-level live region (useAnnouncerStore), which is in the document before any
 * failure arrives, so the first failure is heard as well as a repeat. This button appears with its failure, so it says
 * the failure as it appears; it says it again each time a request settles with the button still on screen (the failure
 * stands); a press clears the region, so the same sentence again is still a change; and it takes its sentence back
 * when it leaves, so the region holds only what is still true.
 *
 * Focus: what the member asked for replaces the failure, and this button with it. When the button leaves while it holds
 * focus, focus moves to `focusTarget` rather than dropping to the document. A button that leaves while the member is
 * elsewhere leaves their focus where it is.
 */
import { nextTick, onBeforeUnmount, onMounted, ref, useId, watch, type ComponentPublicInstance } from 'vue';
import { PhArrowClockwise } from '@phosphor-icons/vue';
import { useAnnouncerStore } from '~/stores/layout/announcer';

// The caller's attributes (a test id, a class) belong on the button, not on the description beside it.
defineOptions({ inheritAttrs: false });

const props = defineProps<{
    /** A request for what failed is in flight. */
    busy: boolean;
    /** The failure, as the member reads it on screen: what this button retries, and what it says when it stands. */
    failure: string;
    /** Where focus goes when what was asked for arrives and replaces this button. */
    focusTarget: () => HTMLElement | null | undefined;
}>();

const emit = defineEmits<{ retry: [] }>();

const button = ref<ComponentPublicInstance | null>(null);
const failureId = useId();
const announcer = useAnnouncerStore();

// What this button last said, which is what it takes back when it leaves.
let said = '';
const say = (message: string) => {
    said = message;
    void announcer.announce(message);
};

const press = () => {
    if (props.busy) return;
    say('');
    emit('retry');
};

// The failure is on screen once its request has settled, so a button that appears idle is that failure arriving. One
// that appears while a request is out says nothing yet: the request's settling does.
onMounted(() => {
    if (!props.busy) say(props.failure);
});

// A settled request removes this button when it brings what was asked for, so settling while it is still here means the
// failure stands.
watch(
    () => props.busy,
    (busy, wasBusy) => {
        if (wasBusy && !busy) say(props.failure);
    },
);

// Runs while the button is still in the document, so it can ask whether it holds focus; the target it hands focus to is
// mounted by the time the render that removes the button has finished.
onBeforeUnmount(() => {
    announcer.withdraw(said);
    if (!button.value || document.activeElement !== button.value.$el) return;
    nextTick(() => props.focusTarget()?.focus());
});
</script>

<style lang="scss" scoped>
.retry {
    &[aria-disabled='true'] {
        cursor: progress;
    }

    // The button sits against containers that clip (a business card, the Data Brokers list's sideways scroll), and the
    // clip cuts the floor's outline, so the ring is drawn inside the button as well (DESIGN.md: --focus-ring-inset
    // where a parent clips). The floor's outline stays.
    &:focus-visible {
        box-shadow: var(--focus-ring-inset);
    }
}
</style>
